/* eslint-disable no-console */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useAuth } from '@clerk/clerk-react';
import { AxiosContext } from './axiosContext.js';

const AxiosProvider = ({ children }) => {
  const { getToken, isLoaded, isSignedIn, userId } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const axiosRef = useRef(null);
  const userIdRef = useRef(null);

  // Create a stable getToken function to avoid recreating interceptors
  const getTokenStable = useCallback(async () => {
    try {
      return await getToken();
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }, [getToken]);

  useEffect(() => {
    const initializeAxios = async () => {
      if (!isLoaded) {
        return;
      }

      // If user signed out, clear the axios instance
      if (!isSignedIn) {
        axiosRef.current = null;
        userIdRef.current = null;
        setIsReady(true);
        return;
      }

      // If axios instance already exists for the same user, don't recreate it
      if (axiosRef.current && userIdRef.current === userId) {
        setIsReady(true);
        return;
      }

      try {
        const baseURL = import.meta.env.VITE_NODE_API_URL;
        if (!baseURL) {
          throw new Error('API URL configuration is missing');
        }
        // Ensure baseURL ends with a slash
        const normalizedBaseURL = baseURL.endsWith('/') ? baseURL : `${baseURL}/`;

        // Create new axios instance
        const axiosInstance = axios.create({
          baseURL: normalizedBaseURL,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // Set up request interceptor to add fresh token to each request
        axiosInstance.interceptors.request.use(
          async (config) => {
            try {
              const freshToken = await getTokenStable();
              if (freshToken) {
                config.headers.Authorization = `Bearer ${freshToken}`;
              }
              return config;
            } catch (error) {
              console.error('Error in request interceptor:', error);
              return Promise.reject(error);
            }
          },
          (error) => {
            console.error('Request interceptor error:', error);
            return Promise.reject(error);
          },
        );

        // Set up response interceptor for error handling
        axiosInstance.interceptors.response.use(
          (response) => response,
          async (error) => {
            if (error.response?.status === 401) {
              console.error('Unauthorized request:', error.config.url);
            }
            return Promise.reject(error);
          },
        );

        axiosRef.current = axiosInstance;
        userIdRef.current = userId;
        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize axios:', error);
        setIsReady(true);
      }
    };

    initializeAxios();
  }, [isLoaded, isSignedIn, userId, getTokenStable]);

  if (!isReady) {
    return null;
  }

  return <AxiosContext.Provider value={axiosRef.current}>{children}</AxiosContext.Provider>;
};

AxiosProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AxiosProvider };
